// src/features/auth/PatientLoginPage.tsx
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Activity, ArrowRight, Smartphone } from "lucide-react";
import { authService } from "../../api/services";
import { useAuthStore } from "./auth.store";
import { AuthUser } from "../../api/types";
import toast from "react-hot-toast";

type Step = "phone" | "otp";

export function PatientLoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<Step>("phone");
  const [otpError, setOtpError] = useState("");

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError("");
    setLoading(true);
    try {
      await authService.sendOtp({ phone });
      setStep("otp");
      toast.success("OTP sent to your phone");
    } catch (err: any) {
      const msg = err.message || "Failed to send OTP. Please try again.";
      // Show inline error instead of toast for actionable messages
      if (
        msg.toLowerCase().includes("book") ||
        msg.toLowerCase().includes("no appointments")
      ) {
        setOtpError(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authService.verifyOtp({ phone, otpCode: otp });
      const user: AuthUser = {
        id: res.patientId,
        name: res.name,
        role: "Patient",
        token: res.token,
        expiresAt: res.expiresAt,
        phone: res.phone,
        patientId: res.patientId,
      };
      login(user);
      toast.success(`Welcome, ${res.name}!`);
      navigate("/");
    } catch (err: any) {
      toast.error(err.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* ── Left panel (branding) ── */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-teal-700 p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, white 1px, transparent 1px),
                              radial-gradient(circle at 75% 75%, white 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />

        <div className="relative flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-white font-bold text-lg leading-none">
              RMV Hospital
            </div>
            <div className="text-teal-200 text-xs mt-0.5">Patient Portal</div>
          </div>
        </div>

        <div className="relative">
          <h1 className="font-display text-5xl font-bold text-white leading-tight mb-4">
            Book Your
            <br />
            <span className="text-emerald-300">Appointment.</span>
          </h1>
          <p className="text-teal-200 text-base leading-relaxed max-w-sm">
            Sign in with your mobile number to book appointments, view your
            history, and manage your healthcare — quick and hassle-free.
          </p>

          <div className="flex gap-8 mt-10">
            {[
              { label: "Quick Login", value: "30s" },
              { label: "Specialties", value: "20+" },
              { label: "Online Booking", value: "24/7" },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-2xl font-bold text-white">
                  {stat.value}
                </div>
                <div className="text-xs text-teal-300 mt-0.5">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative text-xs text-teal-300">
          &copy; {new Date().getFullYear()} RMV Hospital, Bangalore. All rights
          reserved.
        </div>
      </div>

      {/* ── Right panel (form) ── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <Activity className="w-6 h-6 text-teal-600" />
          <span className="font-bold text-slate-900">RMV Hospital</span>
        </div>

        <div className="w-full max-w-md">
          {/* Header badge */}
          <div className="flex items-center gap-2 mb-6">
            <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-teal-50 text-teal-700 border border-teal-100 font-medium">
              <Smartphone className="w-3.5 h-3.5" />
              Patient Login
            </span>
          </div>

          <div className="animate-slide-in">
            <h2 className="text-2xl font-semibold text-slate-900 mb-1">
              {step === "phone" ? "Enter your phone" : "Verify OTP"}
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              {step === "phone"
                ? "We'll send a one-time password to your mobile."
                : `Enter the 6-digit OTP sent to +91 ${phone}`}
            </p>

            {step === "phone" ? (
              <form onSubmit={handleSendOtp} className="space-y-4">
                <div>
                  <label className="label">Mobile Number</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium">
                      +91
                    </span>
                    <input
                      className="input pl-10"
                      type="tel"
                      value={phone}
                      onChange={(e) => {
                        setPhone(
                          e.target.value.replace(/\D/g, "").slice(0, 10),
                        );
                        setOtpError("");
                      }}
                      placeholder="9876543210"
                      pattern="[6-9][0-9]{9}"
                      required
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5">
                    Indian mobile numbers only (10 digits, starting 6-9)
                  </p>
                  {otpError && (
                    <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                      <p className="font-medium">{otpError}</p>
                      <Link
                        to="/public/book"
                        className="mt-2 inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium underline"
                      >
                        Book an Appointment{" "}
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={loading || phone.length !== 10}
                  className="btn btn-primary w-full btn-lg justify-center"
                >
                  {loading ? (
                    "Sending…"
                  ) : (
                    <>
                      <Smartphone className="w-4 h-4" /> Send OTP
                    </>
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div>
                  <label className="label">One-Time Password</label>
                  <input
                    className="input text-center text-2xl font-mono tracking-[0.5em]"
                    type="text"
                    inputMode="numeric"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder="______"
                    maxLength={6}
                    required
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="btn btn-primary w-full btn-lg justify-center"
                >
                  {loading ? (
                    "Verifying…"
                  ) : (
                    <>
                      Verify &amp; Sign in <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setStep("phone")}
                  className="btn btn-ghost w-full justify-center text-xs"
                >
                  &larr; Change phone number
                </button>
              </form>
            )}
          </div>

          {/* Staff login link */}
          <div className="mt-8 text-center">
            <p className="text-xs text-slate-400">
              Hospital staff?{" "}
              <Link
                to="/login"
                className="text-brand-600 hover:text-brand-700 font-medium"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
